import React, { useState } from "react";
import {
  TableCell,
  TableRow,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SyncIcon from "@mui/icons-material/Sync";
import { QRCodeSVG } from "qrcode.react";
import { Product, ProductItem } from "./types/products";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import {
  formatProductItemQuantityWithUnit,
  getProductUnitLabel
} from "./utils/getProductInventoryStatCardDescription";
import { formatProductItemStatus } from "./utils/productUtils";
import { PRODUCT_ITEM_QUANTITY_COLUMN_SX } from "./constants/productItemListTable";

interface ProductItemRowProps {
  item: ProductItem;
  product: Product;
  onView: (itemId: string) => void;
  onCloneItem: (itemId: string) => void;
  onTransition?: (item: ProductItem) => void;
}

const ProductItemRow: React.FC<ProductItemRowProps> = ({
  item,
  product,
  onView,
  onCloneItem,
  onTransition
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useKivunovaTranslation();
  const unitLabel = getProductUnitLabel(product, t);
  const formatQuantity = (quantity: number | undefined | null) =>
    formatProductItemQuantityWithUnit(
      quantity,
      product.sellingMode,
      unitLabel,
      t
    );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    onView(item?.id);
    handleClose();
  };

  const handleCloneItem = () => {
    onCloneItem(item?.id);
    handleClose();
  };

  const handleTransition = () => {
    if (onTransition) {
      onTransition(item);
    }
    handleClose();
  };

  return (
    <TableRow
      hover
      sx={{
        "&:hover": {
          backgroundColor: "action.hover"
        }
      }}
    >
      <TableCell sx={{ fontSize: 13 }}>
        {item.variationName?.trim() || item?.id}
      </TableCell>
      <TableCell
        sx={{
          ...PRODUCT_ITEM_QUANTITY_COLUMN_SX,
          fontSize: 13,
          fontVariantNumeric: "tabular-nums"
        }}
      >
        {formatQuantity(item.totalQuantity)}
      </TableCell>
      <TableCell
        sx={{
          ...PRODUCT_ITEM_QUANTITY_COLUMN_SX,
          fontSize: 13,
          fontVariantNumeric: "tabular-nums"
        }}
      >
        {formatQuantity(item.remainingQuantity)}
      </TableCell>
      <TableCell sx={{ fontSize: 13 }}>
        {formatProductItemStatus(item.state, t)}
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <QRCodeSVG value={item?.id} size={50} />
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ fontSize: 13 }}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            "&:hover": {
              backgroundColor: "action.hover"
            }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          <MenuItem onClick={handleCloneItem}>
            <ListItemIcon>
              <ContentCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t("product-item-row-clone-item", "Clone this item")}
            </ListItemText>
          </MenuItem>
          {onTransition && (
            <MenuItem onClick={handleTransition}>
              <ListItemIcon>
                <SyncIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t("product-item-row-transition-status", "Change status")}
              </ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t("product-item-row-view-details", "View Details")}
            </ListItemText>
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default ProductItemRow;
